import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, ClipboardList, Send } from 'lucide-react';
import { worksCatalog } from '../data/mock';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const Works = () => {
  const { t } = useLanguage();
  const worksCatalogData = t('worksCatalog', worksCatalog);
  const [selectedWorks, setSelectedWorks] = useState([]);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const toggleWork = (item) => {
    setSelectedWorks((prev) => {
      if (prev.includes(item)) {
        return prev.filter((work) => work !== item);
      }
      return [...prev, item];
    });
    setError('');
  };

  const handleRequest = () => {
    if (!selectedWorks.length) {
      setError(t('works.selectError', 'Lūdzu izvēlieties vismaz vienu darbu, lai pieprasītu cenu.'));
      return;
    }

    window.dispatchEvent(
      new CustomEvent('prefill-contact', {
        detail: { services: selectedWorks, source: 'price_request' },
      })
    );

    const contactSection = document.getElementById('kontakti');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="darbi"
      ref={sectionRef}
      className="py-24 section-forest-floor relative overflow-hidden"
      data-testid="works-section"
    >
      <div className="absolute inset-0 forest-grain opacity-40" />
      <div className="absolute inset-0 forest-pattern opacity-20" />
      <div className="absolute top-10 right-10 w-72 h-72 bg-[#1F3D2B]/8 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-10 w-72 h-72 bg-[#1F3D2B]/8 rounded-full blur-3xl" />
      <div className="absolute top-24 left-1/3 w-44 h-2 forest-branch animate-sway" style={{ animationDelay: '1.6s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          className={`text-center sm:text-left mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          data-testid="works-header"
        >
          <span className="inline-flex items-center justify-center sm:justify-start gap-2 text-[#1F3D2B] font-medium text-sm tracking-wider uppercase mb-4 px-4 py-1 bg-[#1F3D2B]/10 rounded-full" data-testid="works-eyebrow">
            <ClipboardList className="w-4 h-4" />
            {t('works.eyebrow', 'Darbi')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-4" data-testid="works-title">
            {t('works.title', 'Darbi')}
          </h2>
          <p className="text-[#2E2E2E]/70 text-lg max-w-3xl mx-auto sm:mx-0" data-testid="works-description">
            {t('works.description', '')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6" data-testid="works-catalog-grid">
          {worksCatalogData.map((category, index) => (
            <div
              key={category.id}
              className={`forest-panel rounded-2xl p-6 border border-[#1F3D2B]/10 shadow-sm transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 120}ms` }}
              data-testid={`works-category-${category.id}`}
            >
              <h3 className="text-xl font-semibold text-[#1F3D2B] mb-2" data-testid={`works-category-title-${category.id}`}>
                {category.title}
              </h3>
              <p className="text-[#2E2E2E]/70 text-sm mb-4" data-testid={`works-category-description-${category.id}`}>
                {category.description}
              </p>
              <div className="space-y-3">
                {category.items.map((item, idx) => {
                  const isSelected = selectedWorks.includes(item);
                  const itemId = `${category.id}-${idx}`;
                  return (
                    <label
                      key={item}
                      htmlFor={`work-${itemId}`}
                      className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                        isSelected
                          ? 'border-[#1F3D2B] bg-[#1F3D2B]/10'
                          : 'border-[#1F3D2B]/10 hover:border-[#1F3D2B]/30'
                      }`}
                      data-testid={`works-item-${itemId}`}
                    >
                      <input
                        id={`work-${itemId}`}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleWork(item)}
                        className="mt-1 h-4 w-4 accent-[#1F3D2B]"
                        data-testid={`works-item-checkbox-${itemId}`}
                      />
                      <div>
                        <span className="text-[#2E2E2E] text-sm font-medium" data-testid={`works-item-label-${itemId}`}>
                          {item}
                        </span>
                        <div className="text-xs text-[#2E2E2E]/60 mt-1">
                          {isSelected ? t('works.itemSelected', 'Pievienots cenu pieprasījumam') : t('works.itemAdd', 'Atzīmējiet, lai pieprasītu cenu')}
                        </div>
                      </div>
                      {isSelected && (
                        <CheckCircle2 className="w-4 h-4 text-[#1F3D2B] ml-auto" />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 forest-panel border border-[#1F3D2B]/10 rounded-2xl p-6" data-testid="works-summary">
          <h4 className="text-lg font-semibold text-[#2E2E2E] mb-3" data-testid="works-summary-title">
            {t('works.summaryTitle', 'Atlasītie darbi')}
          </h4>
          {selectedWorks.length ? (
            <ul className="grid sm:grid-cols-2 gap-2" data-testid="works-summary-list">
              {selectedWorks.map((item, idx) => (
                <li key={item} className="text-sm text-[#2E2E2E]/80 flex items-center gap-2" data-testid={`works-summary-item-${idx}`}>
                  <CheckCircle2 className="w-4 h-4 text-[#1F3D2B]" />
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#2E2E2E]/60" data-testid="works-summary-empty">
              {t('works.summaryEmpty', 'Nav atlasītu darbu. Izvēlieties pakalpojumu sarakstā augstāk.')}
            </p>
          )}

          {error && (
            <div className="mt-4 text-sm text-red-600" data-testid="works-error-message">
              {error}
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleRequest}
              className="forest-cta shadow-lg shadow-[#1F3D2B]/20"
              data-testid="works-request-button"
            >
              {t('works.requestCta', 'Pieprasīt cenu piedāvājumu')}
              <Send className="w-4 h-4 ml-2" />
            </Button>
            <a
              href="#kontakti"
              className="inline-flex items-center justify-center px-6 py-3 forest-cta-outline rounded-xl font-semibold transition-colors"
              data-testid="works-contact-link"
            >
              {t('works.contactCta', 'Sazināties ar mums')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Works;
